/**
 * @component DeviceTable
 * @description Tabella che mostra l'elenco dei dispositivi in riparazione
 * 
 * @param {Object[]} devices - Array di dispositivi da visualizzare
 * @param {Function} onSelectDevice - Callback chiamata quando un dispositivo viene selezionato
 * @param {string} searchTerm - Termine di ricerca per filtrare i dispositivi
 * @param {string} statusFilter - Filtro per lo stato dei dispositivi
 * 
 * @example
 * const devices = [{ id: 1, brand: 'Samsung', model: 'S21' }];
 * 
 * return (
 *   <DeviceTable 
 *     devices={devices}
 *     onSelectDevice={(device) => console.log(device)}
 *     searchTerm=""
 *     statusFilter="all"
 *   />
 * );
 */

import React, { useState, useEffect } from 'react';
import { Table, Badge, Button } from 'react-bootstrap';
import { formatDate, parseDate } from '../../utils/dateUtils';
import TableSkeleton from '../shared/TableSkeleton';
import './DeviceTable.scss';
import { PROBLEM_OPTIONS } from '../../constants';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';

function DeviceTable({ devices, onSelectDevice, filters, onLoadMore, hasMore, loading }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);

  // Simuliamo il caricamento dei dati
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'inRiparazione': return 'warning';
      case 'riparato': return 'info';
      case 'restituito': return 'success';
      case 'ordine': return 'primary';
      case 'nonRiparabile': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'inRiparazione': t('deviceTable.status.inRepair'),
      'riparato': t('deviceTable.status.repaired'),
      'restituito': t('deviceTable.status.returned'),
      'ordine': t('deviceTable.status.order'),
      'nonRiparabile': t('deviceTable.status.notRepairable')
    };
    return statusMap[status] || status;
  };

  const getPaymentStatusBadgeColor = (status) => {
    switch (status) {
      case 'pagato': return 'success';
      case 'acconto': return 'warning';
      case 'nonPagato': return 'danger';
      case 'nonRichiesto': return 'secondary';
      default: return 'secondary';
    }
  };

  const getPaymentStatusLabel = (status) => {
    const paymentMap = {
      'pagato': t('deviceTable.payment.paid'),
      'acconto': t('deviceTable.payment.deposit'),
      'nonPagato': t('deviceTable.payment.notPaid'),
      'nonRichiesto': t('deviceTable.payment.notRequired'),
      'rimborsato': t('deviceTable.payment.refunded')
    };
    return paymentMap[status] || status;
  };

  const getDisplayProblem = (device) => {
    const problems = device.problems || [device.problem];
    if (!problems.length) return '';
    
    const firstProblem = t(`models.device.problems.${problems[0]}`);
    
    if (problems.length > 1) {
      return (
        <>
          {firstProblem}
          <Badge 
            pill 
            className="ms-2 problem-count-badge"
          >
            +{problems.length - 1}
          </Badge>
        </>
      );
    }
    return firstProblem;
  };

  // Cell renderer basato sul valore della colonna
  const renderCell = (device, column) => {
    const value = device[column.key];

    switch (column.key) {
      case 'entryDate':
        return formatDate(value, "dd/MM/yyyy");
      
      case 'deliveryDate':
        // Se il dispositivo è restituito, mostra solo la data senza orario
        if (device.status === 'restituito') {
          return formatDate(value, "dd/MM/yyyy");
        }
        
        // Se la data ha un orario specifico (non 00:00), mostra anche l'orario
        if (value) {
          const date = parseDate(value);
          const hasSpecificTime = date && (date.getHours() !== 0 || date.getMinutes() !== 0);
          return formatDate(value, hasSpecificTime ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy");
        }
        return formatDate(value);
        
      case 'status':
        return (
          <Badge bg={getStatusBadgeColor(value)} className="status-badge">
            {getStatusLabel(value)}
          </Badge>
        );
        
      case 'paymentStatus':
        return (
          <Badge bg={getPaymentStatusBadgeColor(value)} className="payment-badge">
            {getPaymentStatusLabel(value)}
          </Badge>
        );
        
      case 'actions':
        return (
          <Button 
            variant="outline-primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onSelectDevice(device);
            }}
            aria-label={t('deviceTable.viewDetails')}
          >
            <FontAwesomeIcon icon={faEye} />
          </Button>
        );
        
      default:
        return value || '-';
    }
  };

  return (
    <div className="device-table-container">
      {loading && devices.length === 0 ? (
        <TableSkeleton columns={8} rows={5} />
      ) : (
        <>
          <Table hover responsive className="device-table">
            <thead>
              <tr>
                <th className="text-center">{t('deviceTable.columns.repairNumber')}</th>
                <th>{t('deviceTable.columns.brand')}</th>
                <th>{t('deviceTable.columns.model')}</th>
                <th>{t('deviceTable.columns.problem')}</th>
                <th className="text-center">{t('deviceTable.columns.entryDate')}</th>
                <th className="text-center">{t('deviceTable.columns.deliveryDate')}</th>
                <th className="text-center">{t('deviceTable.columns.status')}</th>
                <th className="text-center">{t('deviceTable.columns.payment')}</th>
              </tr>
            </thead>
            <tbody>
              {devices.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-4 text-muted">
                    <i className="fas fa-search me-2"></i>
                    {t('deviceTable.noDevices')}
                  </td>
                </tr>
              ) : (
                devices.map(device => (
                  <tr 
                    key={device.id}
                    onClick={() => onSelectDevice(device)}
                    className="device-row"
                  >
                    <td className="text-center fw-bold">{device.repairNumber}</td>
                    <td>{device.brand}</td>
                    <td>{device.model}</td>
                    <td>{getDisplayProblem(device)}</td>
                    <td className="text-center">{renderCell(device, { key: 'entryDate' })}</td>
                    <td className="text-center">{renderCell(device, { key: 'deliveryDate' })}</td>
                    <td className="text-center">
                      {renderCell(device, { key: 'status' })}
                    </td>
                    <td className="text-center">
                      {renderCell(device, { key: 'paymentStatus' })}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          
          {hasMore && (
            <div className="text-center mt-3">
              <Button 
                variant="outline-primary" 
                onClick={onLoadMore}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    {t('deviceTable.loadMore.loading')}
                  </>
                ) : (
                  t('deviceTable.loadMore.button')
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default DeviceTable; 